import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, PageLayout } from '@sentinel/ui';
import { useUser } from '@sentinel/auth-core';
import { ConfirmDialog } from '../features/security/components/confirm-dialog';
import { SessionRow } from '../features/sessions/components/session-row';
import { type SessionItem, useSessions } from '../features/sessions/use-sessions';
import { useRevokeSession } from '../features/sessions/use-revoke';

const listStyle: React.CSSProperties = {
  display: 'grid',
  gap: '0.5rem',
  margin: 0,
  padding: 0,
  listStyle: 'none',
};

function getStatus(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}

function SessionsPage() {
  const { t } = useTranslation('sessions');
  // useUser keeps the SessionProvider primed, even though we don't read fields.
  useUser();
  const sessions = useSessions();
  const revoke = useRevokeSession();

  const [revokingCurrent, setRevokingCurrent] = useState<SessionItem | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [topError, setTopError] = useState<string | null>(null);
  const [topSuccess, setTopSuccess] = useState<string | null>(null);

  const items = sessions.data ?? [];
  // Sort: backend-flagged current session first, then most recently active.
  const sorted = [...items].toSorted((a, b) => {
    if (a.is_current) return -1;
    if (b.is_current) return 1;
    return Date.parse(b.last_active_at) - Date.parse(a.last_active_at);
  });

  function clearBanners() {
    setTopError(null);
    setTopSuccess(null);
  }

  async function performRevoke(id: string, isCurrent: boolean) {
    clearBanners();
    setRevokingId(id);
    try {
      await revoke.mutateAsync({ id });
      setTopSuccess(t('revoke_success'));
      if (isCurrent) {
        // Backend cleared cookies in the response; bounce to /login.
        window.location.assign('/login');
        return;
      }
    } catch (e) {
      const status = getStatus(e);
      if (status === 403) {
        // Step-up middleware intercepted; the user either cancelled the
        // dialog or step-up failed. Stay quiet — middleware UX has the lead.
        return;
      }
      setTopError(t('revoke_failed'));
    } finally {
      setRevokingId(null);
    }
  }

  function handleRevokeClick(s: SessionItem) {
    if (s.is_current) {
      setRevokingCurrent(s);
      return;
    }
    void performRevoke(s.id, false);
  }

  async function handleConfirmCurrent() {
    if (!revokingCurrent) return;
    const target = revokingCurrent;
    setRevokingCurrent(null);
    await performRevoke(target.id, true);
  }

  return (
    <>
      <PageLayout title={t('title')} subtitle={t('subtitle')}>
        {topError ? (
          <Alert type="error" role="alert">
            {topError}
          </Alert>
        ) : null}
        {topSuccess ? (
          <Alert type="info" role="status">
            {topSuccess}
          </Alert>
        ) : null}

        {sessions.isLoading ? (
          <p>{t('list_loading')}</p>
        ) : sessions.error ? (
          <Alert type="error" role="alert">
            {t('list_failed')}
          </Alert>
        ) : sorted.length === 0 ? (
          <p style={{ margin: 0, color: 'rgba(245,247,255,0.6)' }} data-testid="sessions-empty">
            {t('list_empty')}
          </p>
        ) : (
          <ul style={listStyle} data-testid="sessions-list">
            {sorted.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                isCurrent={s.is_current}
                revoking={revokingId === s.id}
                onRevoke={handleRevokeClick}
              />
            ))}
          </ul>
        )}
      </PageLayout>

      <ConfirmDialog
        open={Boolean(revokingCurrent)}
        title={t('revoke_current_confirm_title')}
        subtitle={t('revoke_current_confirm_message')}
        submitLabel={t('revoke_current_confirm_submit')}
        cancelLabel={t('revoke_current_confirm_cancel')}
        submitting={revoke.isPending}
        variant="danger"
        onSubmit={handleConfirmCurrent}
        onCancel={() => setRevokingCurrent(null)}
      />
    </>
  );
}

export const Route = createFileRoute('/account/sessions')({
  component: SessionsPage,
});
